import React, { useEffect, useState } from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || ''

interface Referral {
  name: string
  date: string
  status: 'trial' | 'paying' | 'registered'
  free_month: boolean
}

interface ReferralData {
  affiliate_code: string
  referrals: Referral[]
  free_months_earned: number
  total_referrals: number
  total_paying: number
  total_clicks?: number
}

const statusLabels: Record<string, { text: string; color: string; bg: string }> = {
  trial: { text: 'בניסיון חינם - ממתין לתשלום', color: '#f59e0b', bg: '#fef3c7' },
  registered: { text: 'נרשם - ממתין לתשלום', color: '#6b7280', bg: '#f3f4f6' },
  paying: { text: 'משלם - חודש חינם זוכה לך! 🎁', color: '#16a34a', bg: '#dcfce7' },
}

export const ReferralPopup: React.FC<{ show: boolean; onClose: () => void }> = ({ show, onClose }) => {
  const [data, setData] = useState<ReferralData | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!show) return
    setLoading(true)
    const authToken = localStorage.getItem('kt-auth-react-v')
    if (!authToken) return
    const parsed = JSON.parse(authToken)
    if (!parsed?.api_token) return

    axios
      .get(`${API_URL}/api/referrals`, {
        headers: { Authorization: `Bearer ${parsed.api_token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [show])

  if (!show) return null

  const referralLink = data ? `${window.location.origin}/auth/register?aff=${data.affiliate_code}` : ''

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)', zIndex: 100000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: '16px', maxWidth: '520px',
          width: '100%', maxHeight: '85vh', overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)', direction: 'rtl',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #2b70fa 0%, #1e5ae0 100%)',
            padding: '24px', color: '#fff', textAlign: 'center',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '12px', left: '12px',
              width: '32px', height: '32px', borderRadius: '50%',
              border: 'none', background: 'rgba(255,255,255,0.2)',
              color: '#fff', cursor: 'pointer', fontSize: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎁</div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>הזמן חבר וקבל חודש חינם</h2>
          <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '14px', lineHeight: 1.6 }}>
            שתף את הלינק שלך לחברים ל-14 יום ניסיון חינם
            <br />
            על כל חבר שמבצע תשלום ראשון — חודש חינם אוטומטית לחשבונך!
          </p>
        </div>

        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>טוען...</div>
          ) : data ? (
            <>
              {/* Referral Link */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
                  הלינק שלך
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    readOnly
                    value={referralLink}
                    style={{
                      flex: 1, padding: '10px 14px', background: '#f9fafb',
                      border: '1px solid #e5e7eb', borderRadius: '8px',
                      fontSize: '13px', color: '#374151', direction: 'ltr',
                      textAlign: 'left',
                    }}
                  />
                  <button
                    onClick={copyLink}
                    style={{
                      padding: '10px 20px', background: copied ? '#16a34a' : '#2b70fa',
                      color: '#fff', border: 'none', borderRadius: '8px',
                      fontWeight: 600, cursor: 'pointer', fontSize: '13px',
                      transition: 'background 0.2s', whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? '✓ הועתק' : 'העתק'}
                  </button>
                </div>
              </div>

              {/* Rules */}
              <div style={{ marginBottom: '24px', background: '#f0f5ff', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e3a8a', marginBottom: '8px' }}>איך זה עובד? 📋</div>
                <ol style={{ margin: 0, paddingInlineStart: '18px', fontSize: '13px', color: '#374151', lineHeight: 1.9 }}>
                  <li>שתפו את הלינק האישי שלכם עם חברים.</li>
                  <li>החבר נרשם דרך הלינק ומקבל 14 יום ניסיון חינם — <b>ללא כרטיס אשראי וללא התחייבות</b>.</li>
                  <li>ברגע שהחבר מבצע את התשלום הראשון — מתווסף לכם <b>חודש חינם</b> אוטומטית.</li>
                  <li>אין הגבלה — חודש חינם על כל חבר משלם! 🎁</li>
                </ol>
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  flex: 1, background: '#eef2ff', borderRadius: '12px',
                  padding: '16px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#6366f1' }}>{data.total_clicks ?? 0}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>קליקים</div>
                </div>
                <div style={{
                  flex: 1, background: '#f0f5ff', borderRadius: '12px',
                  padding: '16px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#2b70fa' }}>{data.total_referrals}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>נרשמו</div>
                </div>
                <div style={{
                  flex: 1, background: '#f0fdf4', borderRadius: '12px',
                  padding: '16px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#16a34a' }}>{data.total_paying}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>משלמים</div>
                </div>
                <div style={{
                  flex: 1, background: '#fef3c7', borderRadius: '12px',
                  padding: '16px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{data.free_months_earned}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>חודשים חינם</div>
                </div>
              </div>

              {/* Share Buttons */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px', display: 'block' }}>
                  שתף את הלינק
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {([
                    {
                      name: 'WhatsApp',
                      bg: '#25D366',
                      icon: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.654-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
                      url: `https://wa.me/?text=${encodeURIComponent('היי, אני משתמש במערכת FBZipper לפרסום אוטומטי בפייסבוק וזה חוסך לי המון זמן! הנה לינק לניסיון חינם ל-14 יום: ' + referralLink)}`,
                    },
                    {
                      name: 'Facebook',
                      bg: '#1877F2',
                      icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
                      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent('היי, אני משתמש במערכת FBZipper לפרסום אוטומטי בפייסבוק וזה חוסך לי המון זמן! הנה לינק לניסיון חינם ל-14 יום:')}`,
                    },
                    {
                      name: 'Telegram',
                      bg: '#0088cc',
                      icon: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
                      url: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent('היי, מכירים את FBZipper? מערכת לפרסום אוטומטי בפייסבוק. הנה לינק לניסיון חינם:')}`,
                    },
                    {
                      name: 'Email',
                      bg: '#6b7280',
                      icon: 'M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z',
                      url: `mailto:?subject=${encodeURIComponent('FBZipper - פרסום אוטומטי בפייסבוק')}&body=${encodeURIComponent('היי,\n\nאני משתמש במערכת FBZipper לפרסום אוטומטי בפייסבוק וזה חוסך לי המון זמן!\n\nהנה לינק לניסיון חינם ל-14 יום:\n' + referralLink)}`,
                    },
                    {
                      name: 'SMS',
                      bg: '#4ade80',
                      icon: 'M7.5 8.25h9m-9 3H12M2.36 12.76c0 3.11 2.04 5.74 4.89 6.62.21.06.44.06.65 0A7.12 7.12 0 0 0 12 21a7.12 7.12 0 0 0 4.1-1.62c.21.06.44.06.65 0 2.85-.88 4.89-3.51 4.89-6.62 0-3.87-3.13-7-7-7h-5.28c-3.87 0-7 3.13-7 7z',
                      url: `sms:?&body=${encodeURIComponent('היי, תנסה את FBZipper - פרסום אוטומטי בפייסבוק. 14 יום ניסיון חינם: ' + referralLink)}`,
                    },
                  ] as const).map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target={s.name === 'Email' || s.name === 'SMS' ? '_self' : '_blank'}
                      rel="noopener noreferrer"
                      title={s.name}
                      style={{
                        width: '44px', height: '44px', borderRadius: '50%',
                        background: s.bg, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer',
                        transition: 'opacity 0.2s, transform 0.2s',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.1)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)' }}
                    >
                      <svg viewBox={s.name === 'Email' || s.name === 'SMS' ? '0 0 24 24' : '0 0 24 24'} width="20" height="20" fill="white">
                        <path d={s.icon} />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>

              {/* Referral List */}
              {data.referrals.length > 0 ? (
                <>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                    ההפניות שלך
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.referrals.map((r, i) => {
                      const st = statusLabels[r.status] || statusLabels.registered
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '12px 16px', background: '#f9fafb',
                            borderRadius: '10px', border: '1px solid #f3f4f6',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937' }}>{r.name}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{formatDate(r.date)}</div>
                          </div>
                          <span
                            style={{
                              padding: '4px 12px', borderRadius: '20px',
                              fontSize: '12px', fontWeight: 600,
                              color: st.color, background: st.bg,
                            }}
                          >
                            {st.text}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: '14px' }}>
                  עוד אין הפניות. שלח את הלינק שלך לחברים!
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#ef4444' }}>שגיאה בטעינת הנתונים</div>
          )}
        </div>
      </div>
    </div>
  )
}
